import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
} from '@chakra-ui/react'
import {CloseIcon} from '@chakra-ui/icons'

import {BottomPanelOpenIcon, LockIcon, UnfoldMoreIcon} from './icons'
import NavTooltip from './NavTooltip'
import {accountType, balanceType, navLinkType} from './types'

// Vertical inset of the floating nav card on the neutral.200 rail field.
// Reskinned page containers use the same value as their top padding so the
// first content card sits flush with the top of the nav card (per the reskin
// page designs, where both tops align at 20px from the viewport).
export const NAV_CARD_INSET = '20px'

// The desktop nav panel floats as a rounded card on the neutral.200 rail field:
// inset 20px top/bottom, an 8px gap from the product rail, with clipped corners.
// The mobile drawer (embedded) keeps a flush, full-height panel instead.
const CARD_CHROME = {
  ml: '8px',
  my: NAV_CARD_INSET,
  h: 'calc(100% - 40px)',
  borderRadius: '4px',
  overflow: 'hidden',
}

const LockBadge = () => (
  <Flex
    align="center"
    justify="center"
    w="24px"
    h="24px"
    borderRadius="full"
    bg="mld.neutral.200"
    flexShrink={0}
  >
    <LockIcon boxSize="16px" color="mld.neutral.800" />
  </Flex>
)

const ComingSoonTag = ({label}) => (
  <Box
    bg="mld.neutral.100"
    color="mld.neutral.800"
    fontSize="10px"
    lineHeight="1.25"
    textTransform="uppercase"
    borderRadius="4px"
    pl="6px"
    pr="8px"
    py="4px"
    flexShrink={0}
  >
    {label}
  </Box>
)

ComingSoonTag.propTypes = {label: PropTypes.string.isRequired}

const NavItem = ({link, comingSoonLabel, collapsed}) => {
  const interactive = !link.comingSoon && !link.locked
  const textColor = (() => {
    if (link.comingSoon) return 'mld.neutral.600'
    if (link.active) return 'mld.neutral.1000'
    return 'mld.neutral.800'
  })()

  // Collapsed: icon-only, square hit area, label moved into a tooltip.
  if (collapsed) {
    return (
      <NavTooltip label={link.label}>
        <Flex
          as={interactive ? 'a' : 'div'}
          href={interactive ? link.href : undefined}
          aria-label={link.label}
          align="center"
          justify="center"
          w="40px"
          h="40px"
          borderRadius="4px"
          bg={link.active ? 'mld.neutral.200' : 'transparent'}
          color={textColor}
          opacity={link.comingSoon ? 0.6 : 1}
          cursor={interactive ? 'pointer' : 'default'}
          _hover={
            interactive && !link.active ? {bg: 'mld.neutral.100'} : undefined
          }
        >
          {React.cloneElement(link.icon, {
            boxSize: '24px',
            filled: link.active,
          })}
        </Flex>
      </NavTooltip>
    )
  }

  return (
    <Flex
      as={interactive ? 'a' : 'div'}
      href={interactive ? link.href : undefined}
      align="center"
      gap="12px"
      w="100%"
      px="12px"
      py="11px"
      borderRadius="4px"
      bg={link.active ? 'mld.neutral.200' : 'transparent'}
      color={textColor}
      cursor={interactive ? 'pointer' : 'default'}
      _hover={interactive && !link.active ? {bg: 'mld.neutral.100'} : undefined}
    >
      {React.cloneElement(link.icon, {boxSize: '24px', filled: link.active})}
      <Text
        fontSize="16px"
        letterSpacing="-0.32px"
        lineHeight="1.5"
        whiteSpace="nowrap"
      >
        {link.label}
      </Text>
      <Spacer />
      {link.comingSoon && <ComingSoonTag label={comingSoonLabel} />}
      {link.locked && !link.comingSoon && <LockBadge />}
    </Flex>
  )
}

NavItem.propTypes = {
  link: navLinkType.isRequired,
  comingSoonLabel: PropTypes.string.isRequired,
  collapsed: PropTypes.bool,
}

NavItem.defaultProps = {collapsed: false}

const Avatar = ({initial, size, fontSize}) => (
  <Flex
    align="center"
    justify="center"
    w={size}
    h={size}
    borderRadius="full"
    bg="mld.neutral.200"
    flexShrink={0}
  >
    <Text fontSize={fontSize} color="mld.neutral.1000">
      {initial}
    </Text>
  </Flex>
)

Avatar.propTypes = {
  initial: PropTypes.string.isRequired,
  size: PropTypes.string,
  fontSize: PropTypes.string,
}

Avatar.defaultProps = {size: '28px', fontSize: '16px'}

// The menu shown when the account row is open: the top businesses (current
// highlighted; selecting another switches to it), then a "View all" link to
// the accounts page. Shared between the expanded row and the collapsed
// avatar trigger.
const AccountMenuList = ({account}) => (
  <MenuList
    py="4px"
    bg="mld.neutral.0"
    border="1px solid"
    borderColor="mld.neutral.200"
    borderRadius="8px"
  >
    {(account.businesses || []).map((business) => (
      <MenuItem
        key={business.key}
        bg={business.current ? 'mld.neutral.100' : 'transparent'}
        _hover={{bg: 'mld.neutral.100'}}
        px="12px"
        py="8px"
        onClick={business.onSelect}
      >
        <Avatar initial={business.initial} size="24px" fontSize="14px" />
        <Text
          ml="12px"
          fontSize="14px"
          letterSpacing="-0.28px"
          fontWeight={business.current ? '500' : '400'}
          color="mld.neutral.900"
          noOfLines={1}
        >
          {business.name}
        </Text>
      </MenuItem>
    ))}
    {account.viewAll && (
      <MenuItem
        as="a"
        href={account.viewAll.href}
        bg={account.viewAll.current ? 'mld.neutral.100' : 'transparent'}
        _hover={{bg: 'mld.neutral.100'}}
        px="12px"
        py="8px"
      >
        <Text
          ml="36px"
          fontSize="14px"
          letterSpacing="-0.28px"
          fontWeight={account.viewAll.current ? '500' : '400'}
          color="mld.neutral.900"
          noOfLines={1}
        >
          {account.viewAll.label}
        </Text>
      </MenuItem>
    )}
  </MenuList>
)

AccountMenuList.propTypes = {account: accountType.isRequired}

// The pinned account row. Given a `businesses` list or a `viewAll` link it
// becomes a menu (see AccountMenuList); otherwise it is a plain button
// firing onClick.
const AccountRow = ({account, collapsed}) => {
  const hasMenu = !!account.businesses?.length || !!account.viewAll

  // Collapsed: just the avatar (name + chevron live in a tooltip / the menu).
  if (collapsed) {
    const avatar = <Avatar initial={account.initial} />
    if (!hasMenu) {
      return (
        <NavTooltip label={account.name}>
          <Box as="button" type="button" onClick={account.onClick}>
            {avatar}
          </Box>
        </NavTooltip>
      )
    }
    return (
      <Menu placement="right-end">
        <NavTooltip label={account.name}>
          <MenuButton>{avatar}</MenuButton>
        </NavTooltip>
        <AccountMenuList account={account} />
      </Menu>
    )
  }

  const summary = (
    <>
      <Avatar initial={account.initial} />
      <Text
        ml="12px"
        fontSize="16px"
        letterSpacing="-0.32px"
        color="mld.neutral.900"
        noOfLines={1}
      >
        {account.name}
      </Text>
      <Spacer />
      <UnfoldMoreIcon boxSize="16px" color="mld.neutral.900" />
    </>
  )

  if (!hasMenu) {
    return (
      <Flex
        as="button"
        type="button"
        align="center"
        w="100%"
        onClick={account.onClick}
      >
        {summary}
      </Flex>
    )
  }

  return (
    <Menu placement="top-start" matchWidth>
      <MenuButton w="100%">
        <Flex align="center" w="100%">
          {summary}
        </Flex>
      </MenuButton>
      <AccountMenuList account={account} />
    </Menu>
  )
}

AccountRow.propTypes = {
  account: accountType.isRequired,
  collapsed: PropTypes.bool,
}

AccountRow.defaultProps = {collapsed: false}

const CollapseToggle = ({collapsed, onToggleCollapse}) => (
  <IconButton
    aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
    variant="ghost"
    size="sm"
    minW="auto"
    h="auto"
    p="0"
    color="mld.neutral.900"
    _hover={{bg: 'transparent', color: 'mld.neutral.700'}}
    icon={
      <BottomPanelOpenIcon
        boxSize="24px"
        transform={collapsed ? 'scaleX(-1)' : undefined}
      />
    }
    onClick={onToggleCollapse}
  />
)

CollapseToggle.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  onToggleCollapse: PropTypes.func.isRequired,
}

// The product-scoped panel: heading (active product name) + a collapse toggle,
// the product's links, then the pinned balance / account footer.
// When `collapsed`, it shrinks to an icon-only rail (labels move to tooltips,
// the balance block is hidden).
const NavPanel = ({
  title,
  showBrand,
  links,
  balance,
  account,
  comingSoonLabel,
  collapsed,
  onToggleCollapse,
  onClose,
}) => {
  if (collapsed) {
    return (
      <Flex
        direction="column"
        align="center"
        flexShrink={0}
        w="88px"
        bg="mld.neutral.50"
        pb="28px"
        {...CARD_CHROME}
      >
        <Flex h="64px" align="center" flexShrink={0}>
          <CollapseToggle collapsed onToggleCollapse={onToggleCollapse} />
        </Flex>
        <Flex direction="column" align="center" gap="12px">
          {links.map((link) => (
            <NavItem
              key={link.label}
              link={link}
              comingSoonLabel={comingSoonLabel}
              collapsed
            />
          ))}
        </Flex>
        <Spacer />
        {account && <AccountRow account={account} collapsed />}
      </Flex>
    )
  }

  // On mobile (rendered in the full-screen drawer) the panel fills the width and
  // the header shows a close button instead of the collapse toggle. On desktop it
  // floats as a rounded card (CARD_CHROME); the drawer stays flush and full height.
  const containerProps = onClose
    ? {flex: '1', minW: '0', h: '100%'}
    : {flexShrink: 0, w: '264px', ...CARD_CHROME}

  return (
    <Flex direction="column" {...containerProps} bg="mld.neutral.50">
      <Flex align="center" h="64px" px="24px">
        <Text
          fontSize="18px"
          fontWeight="500"
          letterSpacing="-0.36px"
          color="mld.neutral.900"
        >
          {showBrand ? 'Marmalade' : title}
        </Text>
        <Spacer />
        {onClose ? (
          <IconButton
            aria-label="Close navigation"
            variant="ghost"
            size="sm"
            minW="auto"
            h="auto"
            p="0"
            color="mld.neutral.900"
            _hover={{bg: 'transparent', color: 'mld.neutral.700'}}
            icon={<CloseIcon boxSize="14px" />}
            onClick={onClose}
          />
        ) : (
          <CollapseToggle
            collapsed={false}
            onToggleCollapse={onToggleCollapse}
          />
        )}
      </Flex>

      <Flex direction="column" gap="12px" px="22px">
        {links.map((link) => (
          <NavItem
            key={link.label}
            link={link}
            comingSoonLabel={comingSoonLabel}
          />
        ))}
      </Flex>

      <Spacer />

      <Flex direction="column" gap="24px" px="24px" pb="28px">
        {balance && (
          <Flex
            align="center"
            bg="mld.neutral.0"
            border="1px solid"
            borderColor="mld.neutral.200"
            borderRadius="4px"
            pl="14px"
            pr="8px"
            py="12px"
          >
            <Box>
              <Text
                fontSize="12px"
                lineHeight="1.25"
                textTransform="uppercase"
                color="mld.neutral.900"
              >
                Balance
              </Text>
              <Flex align="flex-end" gap="4px" mt="4px">
                <Text
                  fontSize="24px"
                  lineHeight="1"
                  letterSpacing="-0.96px"
                  color="mld.neutral.900"
                >
                  {balance.amount}
                </Text>
                <Text
                  fontSize="14px"
                  fontWeight="500"
                  lineHeight="1.25"
                  letterSpacing="-0.14px"
                  textTransform="uppercase"
                  color="mld.neutral.900"
                >
                  {balance.unit}
                </Text>
              </Flex>
            </Box>
          </Flex>
        )}

        {account && <AccountRow account={account} />}
      </Flex>
    </Flex>
  )
}

NavPanel.propTypes = {
  title: PropTypes.string.isRequired,
  // When true, the heading shows the Marmalade wordmark instead of the product
  // name (used when the supplier has a single product, so there's no switching).
  showBrand: PropTypes.bool,
  links: PropTypes.arrayOf(navLinkType).isRequired,
  balance: balanceType,
  account: accountType,
  comingSoonLabel: PropTypes.string,
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  // When set (mobile drawer), the header shows a close button and the panel
  // fills available width instead of the fixed 264px.
  onClose: PropTypes.func,
}

NavPanel.defaultProps = {
  showBrand: false,
  balance: undefined,
  account: undefined,
  comingSoonLabel: 'Coming soon',
  collapsed: false,
  onToggleCollapse: () => {},
  onClose: undefined,
}

export default NavPanel
